import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, TextField, Typography, Paper, Chip, InputAdornment } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CodeIcon from '@material-ui/icons/Code';
import DeveloperModeIcon from '@material-ui/icons/DeveloperMode';
import { useTranslation } from 'react-i18next';

const techStackBackground = `/assets/img/techStack-background.jpg`;
const background = `/assets/img/background.jpg`;
const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    minHeight: '100vh',
    background: `
      radial-gradient(circle at 20% 30%, rgba(0, 245, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(199, 125, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 20%, rgba(57, 255, 20, 0.08) 0%, transparent 50%),
      linear-gradient(135deg, #0a0a0f 0%, #151521 50%, #1a1a2e 100%),
      url(${background})
    `,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    padding: theme.spacing(6, 0),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // Manejo específico para móviles
    [theme.breakpoints.down('md')]: {
      background: `
        radial-gradient(circle at 20% 30%, rgba(0, 245, 255, 0.15) 0%, transparent 50%),
        radial-gradient(circle at 80% 70%, rgba(199, 125, 255, 0.15) 0%, transparent 50%),
        radial-gradient(circle at 40% 20%, rgba(57, 255, 20, 0.1) 0%, transparent 50%),
        linear-gradient(135deg, #0a0a0f 0%, #151521 50%, #1a1a2e 100%)
      `,
      backgroundAttachment: 'scroll',
    },
    [theme.breakpoints.up('md')]: {
      backgroundAttachment: 'fixed',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(45deg, rgba(0, 245, 255, 0.03), rgba(199, 125, 255, 0.03))',
      animation: 'backgroundShift 20s ease-in-out infinite',
      zIndex: 1,
    },
  },
  root: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(0, 3),
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 1),
    },
  },
  searchContainer: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 2,
  },
  input: {
    maxWidth: '500px',
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(10, 15, 30, 0.8)',
      backdropFilter: 'blur(20px)',
      borderRadius: '15px',
      border: '1px solid rgba(0, 245, 255, 0.3)',
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: 'rgba(0, 245, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 245, 255, 0.6)',
        boxShadow: '0 0 20px rgba(0, 245, 255, 0.2)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#00f5ff',
        boxShadow: '0 0 25px rgba(0, 245, 255, 0.4)',
      },
      '& input': {
        color: '#ffffff',
        fontSize: '1.1rem',
        fontFamily: '"Inter", sans-serif',
        padding: theme.spacing(1.5, 0),
      },
    },
    '& .MuiInputLabel-outlined': {
      color: 'rgba(255, 255, 255, 0.7)',
      fontFamily: '"Inter", sans-serif',
      '&.Mui-focused': {
        color: '#00f5ff',
      },
    },
  },
  gridItem: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2.5),
    background: 'rgba(10, 15, 30, 0.7)',
    backdropFilter: 'blur(15px)',
    WebkitBackdropFilter: 'blur(15px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05)',
    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    minHeight: '80px',
    // Estilos específicos para móviles - vista más compacta
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
      minHeight: '60px',
      background: 'rgba(10, 15, 30, 0.85)',
      border: '1px solid rgba(0, 245, 255, 0.3)',
      borderRadius: '12px',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: 'linear-gradient(90deg, transparent, var(--tech-color, #00f5ff), transparent)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '&:hover': {
      transform: 'translateY(-8px) scale(1.03)',
      background: 'rgba(15, 20, 35, 0.85)',
      borderColor: 'var(--tech-color, #00f5ff)',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3), 0 0 30px var(--tech-glow, rgba(0, 245, 255, 0.3))',
      [theme.breakpoints.down('sm')]: {
        transform: 'translateY(-2px) scale(1.01)',
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.4), 0 0 15px var(--tech-glow, rgba(0, 245, 255, 0.4))',
      },
      '&::before': {
        opacity: 1,
      },
      '& $techName': {
        color: '#ffffff',
        textShadow: '0 0 10px var(--tech-color, #00f5ff)',
      },
      '& $techIcon': {
        transform: 'scale(1.2) rotate(10deg)',
        color: 'var(--tech-color, #00f5ff)',
        [theme.breakpoints.down('sm')]: {
          transform: 'scale(1.1) rotate(5deg)',
        },
      },
    },
  },
  techCard: {
    textAlign: 'center',
    width: '100%',
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
    },
  },
  techIcon: {
    fontSize: '2rem',
    color: 'rgba(0, 245, 255, 0.6)',
    marginBottom: theme.spacing(1),
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
      marginBottom: '2px',
    },
  },
  techName: {
    fontSize: '1rem',
    fontWeight: 600,
    fontFamily: '"JetBrains Mono", monospace',
    color: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem',
      letterSpacing: '0.2px',
      lineHeight: 1.2,
      fontWeight: 500,
    },
  },
  notFound: {
    textAlign: 'center',
    padding: theme.spacing(6),
    background: 'rgba(10, 15, 30, 0.8)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    border: '1px solid rgba(255, 107, 107, 0.3)',
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '1.1rem',
    fontFamily: '"Inter", sans-serif',
    marginTop: theme.spacing(3),
    position: 'relative',
    '&::before': {
      content: '"⚠️"',
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      fontSize: '2rem',
    },
  },
  title: {
    textAlign: 'center',
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 700,
    fontSize: '3.2rem',
    background: 'linear-gradient(135deg, #00f5ff, #39ff14, #c77dff)',
    backgroundSize: '300% 300%',
    animation: 'gradientShift 4s ease infinite',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: theme.spacing(2),
    position: 'relative',
    zIndex: 2,
    '&::after': {
      content: '"</>"',
      position: 'absolute',
      top: '-15px',
      right: '-40px',
      fontSize: '2rem',
      color: 'rgba(0, 245, 255, 0.7)',
      opacity: 0.8,
    },
    '&::before': {
      content: '"<"',
      position: 'absolute',
      top: '-15px',
      left: '-40px',
      fontSize: '2rem',
      color: 'rgba(57, 255, 20, 0.7)',
      opacity: 0.8,
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '2.5rem',
      '&::after, &::before': {
        display: 'none', // Ocultar elementos decorativos en tablet
      },
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
      marginBottom: theme.spacing(3),
      '&::after, &::before': {
        display: 'none', // Ocultar elementos decorativos en móvil
      },
    },
  },
  subtitle: {
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '1.2rem',
    fontFamily: '"Inter", sans-serif',
    marginBottom: theme.spacing(5),
    maxWidth: '600px',
    margin: '0 auto',
    marginBottom: theme.spacing(5),
    lineHeight: 1.6,
    position: 'relative',
    zIndex: 2,
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(4),
    flexWrap: 'wrap',
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
      padding: theme.spacing(0, 1),
    },
  },
  statItem: {
    textAlign: 'center',
    background: 'rgba(10, 15, 30, 0.6)',
    backdropFilter: 'blur(15px)',
    padding: theme.spacing(2, 3),
    borderRadius: '15px',
    border: '1px solid rgba(0, 245, 255, 0.2)',
    minWidth: '120px',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5, 2),
      minWidth: '100px',
      background: 'rgba(10, 15, 30, 0.8)',
    },
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    fontFamily: '"JetBrains Mono", monospace',
    background: 'linear-gradient(135deg, #00f5ff, #39ff14)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  statLabel: {
    fontSize: '0.9rem',
    color: 'rgba(255, 255, 255, 0.6)',
    fontFamily: '"Inter", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  topShadow: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '40px',
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
    zIndex: 3,
  },
  bottomShadow: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '40px',
    background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
    zIndex: 3,
  },
  // Animaciones
  '@keyframes gradientShift': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  '@keyframes backgroundShift': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.7 },
  },
  '@keyframes techFloat': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-5px)' },
  },
}));

const TechStack = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [visibleTechs, setVisibleTechs] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  // Hook para detectar tamaño de pantalla
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const techCategories = {
    'Backend': {
      color: '#00f5ff',
      glow: 'rgba(0, 245, 255, 0.3)',
      techs: ['Java', 'Spring', 'Spring Boot', 'JPA', 'Hibernate', '.NET Framework', 'ASP.NET', 'ADO.NET', 'C#']
    },
    'Database': {
      color: '#39ff14',
      glow: 'rgba(57, 255, 20, 0.3)',
      techs: ['SQL Server', 'PostgreSQL', 'MySQL', 'RDBMS', 'NON-RDBMS', 'PL/SQL', 'Redis', 'MongoDB']
    },
    'DevOps & Cloud': {
      color: '#c77dff',
      glow: 'rgba(199, 125, 255, 0.3)',
      techs: ['Git', 'Docker', 'Kubernetes', 'AWS', 'Azure', 'n8n', 'SonarQube']
    },
    'Frontend': {
      color: '#e0aaff',
      glow: 'rgba(224, 170, 255, 0.3)',
      techs: ['JavaScript', 'TypeScript', 'React', 'Angular', 'HTML5', 'CSS3', 'Bootstrap', 'Material-UI']
    },
    'Artificial Intelligence': {
      color: '#ff1744',
      glow: 'rgba(255, 23, 68, 0.3)',
      techs: ['OpenAI API', 'ChatGPT', 'Claude', 'GitHub Copilot', 'MCP', 'Prompt Engineering']
    },
    'Architecture': {
      color: '#ff6b35',
      glow: 'rgba(255, 107, 53, 0.3)',
      techs: ['Design Patterns', 'Architecture Patterns']
    }
  };

  // Flatten all techs with their categories
  const allTechs = Object.entries(techCategories).flatMap(([category, data]) =>
    data.techs.map(tech => ({
      name: tech,
      category,
      color: data.color,
      glow: data.glow
    }))
  );

  const filteredTechs = allTechs.filter((tech) =>
    tech.name.toLowerCase().includes(filter.toLowerCase()) ||
    tech.category.toLowerCase().includes(filter.toLowerCase())
  );

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  // Animation effect for tech cards
  useEffect(() => {
    setVisibleTechs([]);
    const timer = setTimeout(() => {
      setVisibleTechs(filteredTechs);
    }, 100);
    return () => clearTimeout(timer);
  }, [filter]);

  const getTechIcon = (tech) => {
    const iconMap = {
      'Java': '☕',
      'JavaScript': '🟨',
      'React': '⚛️',
      'Docker': '🐳',
      'AWS': '☁️',
      'Azure': '☁️',
      'Git': '📚',
      'Spring': '🍃',
      'MongoDB': '🍃',
      'PostgreSQL': '🐘',
      'MySQL': '🐬',
      'Redis': '🔴',
      'SQL Server': '💾'
    };
    return iconMap[tech] || '💻';
  };

  return (
    <div className={classes.container}>
      <div className={classes.topShadow}></div>
      
      <div className={classes.root}>
        <h2 className={classes.title}>{t('techStack.title')}</h2>
        
        <Typography className={classes.subtitle}>
          {t('techStack.subtitle')}
        </Typography>

        <div className={classes.statsContainer}>
          <div className={classes.statItem}>
            <div className={classes.statNumber}>{allTechs.length}</div>
            <div className={classes.statLabel}>{t('techStack.stats.technologies')}</div>
          </div>
          <div className={classes.statItem}>
            <div className={classes.statNumber}>{Object.keys(techCategories).length}</div>
            <div className={classes.statLabel}>{t('techStack.stats.categories')}</div>
          </div>
          <div className={classes.statItem}>
            <div className={classes.statNumber}>{filteredTechs.length}</div>
            <div className={classes.statLabel}>{t('techStack.stats.results')}</div>
          </div>
        </div>

        <div className={classes.searchContainer}>
          <TextField
            label={t('techStack.search.label')}
            variant="outlined"
            fullWidth
            className={classes.input}
            value={filter}
            onChange={handleFilterChange}
            placeholder={t('techStack.search.placeholder')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: 'rgba(0, 245, 255, 0.7)' }} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <Grid container spacing={isMobile ? 1 : 3}>
          {(visibleTechs.length > 0 ? visibleTechs : filteredTechs).map((tech, index) => (
            <Grid item xs={6} sm={6} md={4} lg={3} key={index}>
              <Paper
                className={classes.gridItem}
                style={{
                  '--tech-color': tech.color,
                  '--tech-glow': tech.glow,
                  animationDelay: `${index * 0.1}s`
                }}
                elevation={0}
              >
                <div className={classes.techCard}>
                  <div 
                    className={classes.techIcon}
                    style={{ 
                      color: tech.color,
                      fontSize: '2.5rem'
                    }}
                  >
                    {getTechIcon(tech.name)}
                  </div>
                  <Typography className={classes.techName}>
                    {tech.name}
                  </Typography>
                  {!isMobile && (
                    <Chip
                      label={tech.category}
                      size="small"
                      style={{
                        backgroundColor: `${tech.color}20`,
                        color: tech.color,
                        fontSize: '0.7rem',
                        fontFamily: '"Inter", sans-serif',
                        marginTop: '8px',
                        border: `1px solid ${tech.color}40`
                      }}
                    />
                  )}
                </div>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {filteredTechs.length === 0 && (
          <div className={classes.notFound}>
            <Typography variant="h6" style={{ marginTop: '40px', fontFamily: '"Inter", sans-serif' }}>
              {t('techStack.notFound.title')}
            </Typography>
            <Typography style={{ marginTop: '10px', opacity: 0.8 }}>
              {t('techStack.notFound.description')}
            </Typography>
          </div>
        )}
      </div>
      
      <div className={classes.bottomShadow}></div>
    </div>
  );
};

export default TechStack;
